import MojitoCore from 'mojito/core';
import MojitoModules from 'mojito/modules';
import MojitoServices from 'mojito/services';
import MojitoApplications from 'mojito/applications';

import {INTENT_TYPES} from '#core/utils/intent-types.js';

import {AbstractFeature, allFeatures} from '#core/application/abstract-feature.js';
import {Intents} from '#core/application/intents/index.js';
import {ApplicationConfig} from '#core/config/application-config.js';

import {AUTH_METHOD} from './types.js';
import {wrapExternal} from './utils.js';
import {MessageId, postMessage} from './messaging.ts';

const {dispatch} = MojitoCore.Services.redux.store;
const reduxInstance = MojitoCore.Services.redux;
const loginActions = MojitoModules.Login.actions;
const IntentActions = MojitoCore.Intents.actions;
const authenticationActions = MojitoServices.Authentication.actions;
const {isLoggedIn} = MojitoServices.Authentication.selectors;

class ActionsProxyStorage {
    constructor() {
        this.wasLoggedIn = false; // it is needed to skip first logout (from undefined login state)
        this.onLoginHandler = null; // Intent or function
        this.onLogoutHandler = null;
        this.onDepositHandler = null;
        this.onNavigateHostHandler = null;

        this.overlayPayload = undefined;

        reduxInstance.actionListener.startListening({
            actionCreator: loginActions.showLoginView,
            effect: this.handleShowLoginView.bind(this),
        });

        reduxInstance.actionListener.startListening({
            actionCreator: authenticationActions.loginSuccess,
            effect: this.handleLoginSuccess.bind(this),
        });

        reduxInstance.actionListener.startListening({
            actionCreator: authenticationActions.logoutSuccess,
            effect: this.handleLogout.bind(this),
        });

        reduxInstance.actionListener.startListening({
            actionCreator: IntentActions.publishIntent,
            effect: this.handlePublishIntent.bind(this),
        });

        if (!IS_LOTTO) {
            const {actions: applicationActions} = MojitoApplications.Sports;
            reduxInstance.actionListener.startListening({
                actionCreator: applicationActions.activeSlidingType,
                effect: this.handleActiveSlidingType.bind(this),
            });
        }
    }

    handleShowLoginView() {
        if (this.onLoginHandler) {
            invokeHandler(this.onLoginHandler);

            return false;
        }

        if (Intents.LOGIN) {
            invokeHandler(Intents.LOGIN);

            return false;
        }
    }

    handleLoginSuccess() {
        this.wasLoggedIn = true;
        postMessage({id: MessageId.AUTHENTICATION_STATE, authenticated: true});
    }

    handleLogout() {
        if (!this.wasLoggedIn) return false; // Never been logged in, so its logout from undefined state

        postMessage({id: MessageId.AUTHENTICATION_STATE, authenticated: false});

        if (this.onLogoutHandler) {
            invokeHandler(this.onLogoutHandler);

            return false;
        }

        if (Intents.LOGOUT) {
            invokeHandler(Intents.LOGOUT);

            return false;
        }
    }

    handlePublishIntent(action) {
        const {payload: intent} = action;
        // in case there was intent published that forces user to login,
        // we will show login view unless user is already authenticated
        if (intent?.data?.authRequired && !isLoggedIn()) {
            return this.handleShowLoginView();
        }

        switch (intent.type) {
            case INTENT_TYPES.PROXY_SHOW_LOGIN: {
                return this.handleShowLoginView();
            }

            case INTENT_TYPES.PROXY_SHOW_DEPOSIT: {
                if (this.onDepositHandler) {
                    invokeHandler(this.onDepositHandler);

                    return false;
                }

                // Behavior differs for mobile/desktop
                // Intents.DEPOSIT / Account popup  must be configured
                const DEPOSIT_INTENT = Intents.DEPOSIT;
                dispatch(IntentActions.publishIntent(DEPOSIT_INTENT.type, DEPOSIT_INTENT.data));

                return false;
            }

            case INTENT_TYPES.NAVIGATE_HOST: {
                const {url, options = {}} = intent.data || {};
                if (!this.onNavigateHostHandler) {
                    // window.location.assign(url); // Do nothing in no host configured
                } else {
                    invokeHandler(this.onNavigateHostHandler, url, options);
                }
            }
        }
    }

    handleActiveSlidingType(action) {
        const changed = this.overlayPayload !== action.payload;
        if (!changed) return;

        this.overlayPayload = action.payload;

        postMessage({id: MessageId.OVERLAY_STATE, shown: action.payload !== undefined});
    }

    // SHOW_LOGIN
    setShowLoginHandler(intent) {
        this.onLoginHandler = wrapExternal(intent);

        const hasOverrideDefault =
            !!intent || !!Intents.LOGIN || ApplicationConfig.initParams.authMethod === AUTH_METHOD.EXTERNAL;
        dispatch(loginActions.configure({ignoreShowLoginView: hasOverrideDefault}));
    }

    // LOG_OUT
    setLogoutHandler(intent) {
        this.onLogoutHandler = wrapExternal(intent);
    }

    // SHOW_DEPOSIT
    setDepositHandler(intent) {
        this.onDepositHandler = wrapExternal(intent);
    }

    // NAVIGATE
    setNavigateHostHandler(handler) {
        this.onNavigateHostHandler = wrapExternal(handler);
    }

    dispose() {
        this.onDepositHandler = null;
        this.onLoginHandler = null;
        this.onLogoutHandler = null;
        this.onNavigateHostHandler = null;

        this.wasLoggedIn = false;
        this.overlayPayload = undefined;
    }
}

function invokeHandler(handler, ...params) {
    if (!handler) {
        return;
    }

    switch (typeof handler) {
        case 'object':
            // it is an intent
            dispatch(IntentActions.publishIntent(handler.type, handler.data));
            break;
        case 'function':
            handler(...params);
            break;
        default:
        // do nothing
    }
}

// Always has instance
export const actionsProxyStorage = new ActionsProxyStorage();

class ActionsProxyFeature extends AbstractFeature {
    get name() {
        return 'Login/Deposit Proxy';
    }

    afterMojito() {
        actionsProxyStorage.setShowLoginHandler(actionsProxyStorage.onLoginHandler); // just trigger update store

        return super.afterMojito();
    }

    dispose() {
        actionsProxyStorage.dispose();
        super.dispose();
    }
}

new ActionsProxyFeature(allFeatures);
